import { Button, Dialog, DialogActions, DialogContent, DialogTitle, Stack } from '@mui/material';
import { Form, Formik, type FormikHelpers } from 'formik';
import { useDispatch, useSelector } from 'react-redux';

import SubmitButton from 'components/common/formikForm/SubmitButton';
import { SignatureMetadataFields } from 'views/v2/signatures/components/signatureMetadataFields';

import { setAlert } from 'redux/alert';
import { updateSignature } from 'redux/signatures/signatures.middleware';
import { selectSignaturesUpdating } from 'redux/signatures/signatures.slice';
import type { AppDispatch } from 'redux/store';
import type { AgentSignature } from 'types';
import { getErrorMessage } from 'utils/v2/errorHelper';
import { editSignatureValidationSchema } from 'utils/validations/signature';

interface EditSignatureDialogProps {
    signature: AgentSignature;
    onClose: () => void;
}

export default function EditSignatureDialog({ signature, onClose }: EditSignatureDialogProps) {
    const dispatch = useDispatch<AppDispatch>();
    const isUpdating = useSelector(selectSignaturesUpdating);

    const handleSubmit = async (
        values: AgentSignature,
        _helpers: FormikHelpers<AgentSignature>
    ) => {
        try {
            await dispatch(
                updateSignature({
                    id: signature.id,
                    updates: {
                        display_name: values.display_name.trim(),
                        title: values.title.trim(),
                        signature_name: values.signature_name.trim(),
                    },
                })
            ).unwrap();
            dispatch(setAlert({ message: 'Signature updated successfully', type: 'success' }));
            onClose();
        } catch (error) {
            dispatch(
                setAlert({
                    message: getErrorMessage(error, 'Failed to update signature'),
                    type: 'error',
                })
            );
        }
    };

    return (
        <Dialog
            open
            maxWidth="sm"
            fullWidth
            scroll="paper"
            slotProps={{
                paper: {
                    sx: {
                        borderRadius: 2,
                        display: 'flex',
                        flexDirection: 'column',
                        m: 2,
                        maxHeight: { xs: '100%', sm: '90vh' },
                        width: { xs: '100%', sm: '100%' },
                    },
                },
            }}
        >
            <Formik
                key={signature.id}
                enableReinitialize
                initialValues={signature}
                validationSchema={editSignatureValidationSchema}
                onSubmit={handleSubmit}
            >
                <Form
                    style={{
                        display: 'flex',
                        flexDirection: 'column',
                        minHeight: 0,
                        flex: 1,
                        overflow: 'hidden',
                    }}
                >
                    <DialogTitle sx={{ fontWeight: 700, flexShrink: 0, py: 2 }}>
                        Edit signature details
                    </DialogTitle>

                    <DialogContent
                        dividers
                        sx={{
                            flex: '1 1 auto',
                            minHeight: 0,
                            overflowY: 'auto',
                            px: 3,
                            py: 2.5,
                        }}
                    >
                        <Stack spacing={1}>
                            <SignatureMetadataFields disabled={isUpdating} showDefault={false} />
                        </Stack>
                    </DialogContent>

                    <DialogActions
                        sx={{
                            flexShrink: 0,
                            px: 3,
                            py: 2,
                            gap: 1,
                        }}
                    >
                        <Button
                            variant="outlined"
                            onClick={onClose}
                            disabled={isUpdating}
                            sx={{ textTransform: 'none' }}
                        >
                            Cancel
                        </Button>
                        <SubmitButton loading={isUpdating} sx={{ textTransform: 'none' }}>
                            Save Changes
                        </SubmitButton>
                    </DialogActions>
                </Form>
            </Formik>
        </Dialog>
    );
}
